import { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import AuthContext, { availableMenuItems } from '../../context/authContext';
import { startRegistration } from '@simplewebauthn/browser';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  Switch,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useUser from '../../hooks/useUser';
import useWebAuthnApi from '../../hooks/useWebAuthnApi';
import useHttp from '../../hooks/useHttp';
import useEntertainment from '../../hooks/useEntertainment';
import useDeviceTypeRequest from '../../hooks/useDeviceTypeRequest';
import useRole from '../../hooks/useRole';
import { toast } from 'react-toastify';
import { useColorMode } from '../../context/colorModeContext';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import { deviceTypeLabels, getDeviceInstallId } from '../../util/deviceInfo';
import { useNoPizzaWarnings } from '../../util/pizzaWarnings';

function hasRoleSuperAdmin(roles) {
  if (!Array.isArray(roles)) return false;
  return roles.some(r => {
    if (typeof r === 'string') return r === 'superAdmin';
    if (r && typeof r === 'object') return r.name === 'superAdmin' || r.readableName === 'superAdmin';
    return false;
  });
}

// Additional privileged roles a superAdmin can grant every top-level right to
// with one click, mirroring the superAdmin grant. Must stay in sync with the
// backend allowlist (GRANT_ALL_RIGHTS_ALLOWED_ROLES in role.handlers.js).
const ADDITIONAL_GRANTABLE_ROLES = [
  { name: 'executiveLeadership', label: 'executiveLeadership' },
  { name: 'ITManager', label: 'ITManager' },
];

// Shared styling for the collapsible settings sections so the page only reveals
// one group at a time instead of one long scroll of every option.
const sectionAccordionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  mb: 1.5,
  '&:before': { display: 'none' },
  '&.Mui-expanded': { mb: 1.5 },
};

const sectionSummarySx = {
  '& .MuiAccordionSummary-content': { my: 1.5 },
};

const subSectionTitleSx = {
  mb: 1,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  fontSize: 13,
};

export default function Settings() {
  const authCtx = useContext(AuthContext);
  const { updateUserPreferences, getDevAdyenBypassToken } = useUser();
  const {
    webauthnRegisterStartAuth,
    webauthnRegisterFinishAuth,
    getWebauthnStatus,
    getWebauthnCredentials,
    deleteWebauthnCredential,
  } =
    useWebAuthnApi();
  const userId = authCtx?.currentUser?.userId;
  const userName = authCtx?.currentUser?.userName || '';
  const userRoles = authCtx?.currentUser?.roles || [];
  const [localPrefs, setLocalPrefs] = useState(() => ({
    showSuccessToastsInDataViews: true,
    ctrlFRedirectTarget: 'off',
    ctrlFOverrideInDataViews: true,
    hideMailSendBackgroundActionHint: false,
    ...(authCtx?.preferences || {}),
  }));
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [tokenDialogValue, setTokenDialogValue] = useState('');
  const [tokenCopyLoading, setTokenCopyLoading] = useState(false);
  const [webauthnStatus, setWebauthnStatus] = useState(null);
  const [webauthnCredentials, setWebauthnCredentials] = useState([]);
  const [newCredentialName, setNewCredentialName] = useState('');
  const [registeringCredential, setRegisteringCredential] = useState(false);
  const [deletingCredentialId, setDeletingCredentialId] = useState('');

  const [slotRecalcDialogOpen, setSlotRecalcDialogOpen] = useState(false);
  const [slotRecalcEntertainments, setSlotRecalcEntertainments] = useState([]);
  const [slotRecalcSelectedId, setSlotRecalcSelectedId] = useState('');
  const [slotRecalcLoading, setSlotRecalcLoading] = useState(false);
  const [slotRecalcEntertainmentsLoaded, setSlotRecalcEntertainmentsLoaded] = useState(false);
  const [slotRecalcEntertainmentsLoading, setSlotRecalcEntertainmentsLoading] = useState(false);

  const { sendRequest } = useHttp();
  const { getEntertainments } = useEntertainment();
  const { requestDeviceType, getMyDeviceTypeRequests, getMyDevices, revokeMyDevice } =
    useDeviceTypeRequest();
  const { grantSuperAdminAllRights, grantRoleAllRights } = useRole();

  // SuperAdmin: one-click grant of all top-level rights to the superAdmin role
  const [superAdminGrantDialogOpen, setSuperAdminGrantDialogOpen] = useState(false);
  const [superAdminGrantLoading, setSuperAdminGrantLoading] = useState(false);

  // SuperAdmin: one-click grant of all top-level rights to further privileged
  // roles (executiveLeadership, ITManager). pendingGrantRole holds the role name
  // awaiting confirmation, or null when the dialog is closed.
  const [pendingGrantRole, setPendingGrantRole] = useState(null);
  const [roleGrantLoading, setRoleGrantLoading] = useState(false);

  // Device type request flow (settings entry point)
  const canRequestDeviceType = authCtx.checkRight('deviceTypeRequest:create');
  const [deviceRequestDialogOpen, setDeviceRequestDialogOpen] = useState(false);
  const [deviceRequestType, setDeviceRequestType] = useState('counter');
  const [deviceRequestLabel, setDeviceRequestLabel] = useState('');
  const [deviceRequestReason, setDeviceRequestReason] = useState('');
  const [deviceRequestSubmitting, setDeviceRequestSubmitting] = useState(false);
  const [myDeviceRequests, setMyDeviceRequests] = useState([]);
  const [myDevices, setMyDevices] = useState([]);
  const currentInstallId = getDeviceInstallId();
  const muiTheme = useTheme();
  const isNarrowDeviceTables = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const isStaging = import.meta.env.VITE_IS_STAGING === 'true';
  const isDev = import.meta.env.VITE_IS_DEV === 'true' || Boolean(import.meta.env.DEV);
  const isDevOrStaging = isDev || isStaging;
  const isSuperAdmin = hasRoleSuperAdmin(userRoles);
  const showDevAdyenBypass = Boolean(isDevOrStaging && isSuperAdmin);
  const { colorModePreference, setColorModePreference } = useColorMode();
  // Device-local pizza pop-up opt-out (localStorage only, not synced to backend).
  const [suppressPizzaWarnings, setSuppressPizzaWarnings] = useNoPizzaWarnings();

  const hasSlotRecalcFull = authCtx.checkRight('booking:slots:recalc');
  const hasSlotRecalcOwn = authCtx.checkRight('booking:slots:recalc:own');
  const showSlotRecalc = hasSlotRecalcFull || hasSlotRecalcOwn;
  const showToolsSection = showDevAdyenBypass || showSlotRecalc;

  // Sidebar counters this user may hide: every menu item carrying a countKey for
  // which the user holds the matching `deactivateCounter:<key>` right. Derived from
  // the menu definition so the list tracks whichever counters exist.
  const counterToggleItems = useMemo(() => {
    const seen = new Set();
    const items = [];
    for (const item of availableMenuItems) {
      if (!item.countKey || seen.has(item.countKey)) continue;
      if (!authCtx.checkRight(`deactivateCounter:${item.countKey}`)) continue;
      seen.add(item.countKey);
      items.push({ key: item.countKey, label: item.name });
    }
    return items;
  }, [authCtx]);

  // Sync localPrefs with AuthContext preferences when they change
  useEffect(() => {
    if (authCtx?.preferences) {
      setLocalPrefs(prevPrefs => ({
        ...prevPrefs,
        ...authCtx.preferences,
      }));
    }
  }, [authCtx?.preferences]);

  const reloadWebauthnData = useCallback(async () => {
    try {
      const [status, credentialsResponse] = await Promise.all([
        getWebauthnStatus(),
        getWebauthnCredentials(),
      ]);
      setWebauthnStatus(status || null);
      setWebauthnCredentials(Array.isArray(credentialsResponse?.credentials) ? credentialsResponse.credentials : []);
    } catch (_error) {
      setWebauthnStatus(null);
      setWebauthnCredentials([]);
    }
  }, [getWebauthnCredentials, getWebauthnStatus]);

  useEffect(() => {
    if (authCtx?.isLoggedIn) {
      reloadWebauthnData();
    }
  }, [authCtx?.isLoggedIn, reloadWebauthnData]);

  // Settings are grouped into a few top-level categories. Each category renders
  // as a collapsible accordion; categories with more than one sub-group show the
  // sub-group titles as headers inside the accordion.
  const settingsCategories = [
    {
      id: 'profile',
      title: 'Profil & Sichtbarkeit',
      subgroups: [
        {
          title: 'Geburtstag',
          items: [
            {
              key: 'showBirthday',
              label: 'Meinen Geburtstag im Team-Widget anzeigen',
              type: 'switch',
            },
            {
              key: 'hideBirthdayAge',
              label: 'Mein Alter verbergen',
              type: 'switch',
            },
          ],
        },
      ],
    },
    {
      id: 'interface',
      title: 'Oberfläche & Navigation',
      subgroups: [
        {
          title: 'Seitennavigation',
          items: [
            {
              key: 'mailSidebarCountOnlyAssigned',
              label: 'Mail-Zähler nur für eigene Gruppen zählen',
              type: 'switch',
            },
            {
              key: 'sidebarGroupsExpandedByDefault',
              label: 'Gruppen standardmäßig ausgeklappt',
              type: 'switch',
            },
            {
              key: 'showSidebarMenuFilter',
              label: 'Filterfeld anzeigen',
              type: 'switch',
            },
          ],
        },
        {
          title: 'Suche',
          items: [
            {
              key: 'ctrlFRedirectTarget',
              value: 'sidebarMenu',
              label: 'Strg+F auf die Menüsuche in der Sidebar umleiten',
              type: 'exclusiveSwitch',
            },
            {
              key: 'ctrlFRedirectTarget',
              value: 'headerSearch',
              label: 'Strg+F auf die Suche im Header umleiten',
              type: 'exclusiveSwitch',
            },
          ],
        },
        {
          title: 'Verwaltung',
          items: [
            {
              key: 'showSuccessToastsInDataViews',
              label: 'Erfolgsmeldungen in Datenbankansichten anzeigen',
              type: 'switch',
            },
            {
              key: 'ctrlFOverrideInDataViews',
              label: 'In Datenbankansichten Strg + F überschreiben',
              type: 'switch',
            },
          ],
        },
        {
          title: 'Assistent',
          items: [
            {
              key: 'showAgentInfoBox',
              label: 'Assistent: Hinweisbox anzeigen',
              type: 'switch',
            },
          ],
        },
      ],
    },
    {
      id: 'bookings',
      title: 'Buchungen',
      subgroups: [
        {
          title: 'Buchungen',
          items: [
            {
              key: 'onsiteQrDefaultMode',
              label: 'Onsite QR: PIN standardmäßig verwenden',
              type: 'modeSwitch',
            },
            {
              key: 'openBookingAfterCreate',
              label: 'Buchung nach dem Speichern automatisch öffnen',
              type: 'switch',
            },
            {
              key: 'showMailDeliveryStep',
              label: 'Nach dem Speichern auf die Zustellung der Bestätigungsmail warten',
              type: 'switch',
            },
            {
              key: 'startTimePricePerPerson',
              label: 'Startzeiten: Preis pro Person anzeigen (statt Gesamtpreis)',
              type: 'switch',
            },
            {
              key: 'internalVerticalShowDetails',
              label: 'Detailansicht in Bash und Golf standardmäßig aktiv',
              type: 'switch',
            },
            {
              key: 'golfVerticalShowResourceSwitchCards',
              label: 'Golf-Detailansicht: Ressourcenwechsel als eigene Karte anzeigen',
              type: 'switch',
            },
            {
              key: 'golfVerticalShowStaggeredGroupCards',
              label: 'Golf-Detailansicht: gestaffelte Gruppen in mehreren Zeitslots anzeigen',
              type: 'switch',
            },
            {
              key: 'showWeekdayInBookingsView',
              label: 'In der Buchungsansicht den Wochentag im Datumsfeld anzeigen',
              type: 'switch',
            },
            {
              key: 'showWeekdayInResourcesView',
              label: 'In der Auslastungsansicht den Wochentag im Datumsfeld anzeigen',
              type: 'switch',
            },
          ],
        },
      ],
    },
    {
      id: 'notifications',
      title: 'Benachrichtigungen',
      subgroups: [
        {
          title: 'E-Mails',
          items: [
            {
              key: 'hideMailSendBackgroundActionHint',
              label: 'Hinweis auf Hintergrundaktionen beim Mailversand anzeigen',
              type: 'inverseSwitch',
            },
          ],
        },
        {
          title: 'Pizza',
          items: [
            {
              key: 'pizzaPopupNotifications',
              label: 'Pizzameldung als Pop-up erhalten',
              type: 'switch',
            },
            {
              key: 'noPizzaWarnings',
              label: 'Pizzameldungen auf diesem Gerät unterdrücken',
              hint: 'Gilt nur für dieses Gerät (lokal gespeichert) und überstimmt die Pop-up-Einstellung.',
              type: 'deviceSwitch',
            },
          ],
        },
      ],
    },
  ];

  const showCtrlShiftFHint =
    localPrefs?.ctrlFRedirectTarget !== 'off' && localPrefs?.ctrlFOverrideInDataViews !== false;

  const persistPreferences = useCallback(
    async updatedPrefs => {
      if (!userId) return;
      try {
        const updated = await updateUserPreferences(updatedPrefs);
        if (updated && updated.preferences) {
          authCtx.setPreferences(updated.preferences);
        }
      } catch (error) {
        toast.error('Speichern der Einstellungen fehlgeschlagen');
        throw error;
      }
    },
    [userId, authCtx, updateUserPreferences]
  );

  const handleToggle = useCallback(
    async (name, nextChecked) => {
      const previousPrefs = localPrefs;
      const updatedPrefs = { ...localPrefs, [name]: Boolean(nextChecked) };
      setLocalPrefs(updatedPrefs);
      try {
        await persistPreferences(updatedPrefs);
        if (name === 'devBypassAdyen') {
          toast.info(Boolean(nextChecked) ? 'Adyen-BYPASS aktiviert (nur Dev/Staging)' : 'Adyen-BYPASS deaktiviert');
        }
      } catch (_e) {
        setLocalPrefs(previousPrefs);
      }
    },
    [localPrefs, persistPreferences, getDevAdyenBypassToken]
  );

  const copyDevBypassToken = useCallback(async () => {
    try {
      setTokenCopyLoading(true);
      const tokenRes = await getDevAdyenBypassToken();
      const token = tokenRes && tokenRes.token ? String(tokenRes.token) : '';
      if (!token) {
        toast.error('Token konnte nicht geladen werden');
        return;
      }

      try {
        await navigator.clipboard.writeText(token);
        toast.success(
          "Token kopiert. Im Booking-Frontend unter localStorage-Key 'fzDevAdyenBypassToken' speichern."
        );
      } catch (_e) {
        setTokenDialogValue(token);
        setTokenDialogOpen(true);
        toast.info('Zwischenablage nicht verfügbar – Token wird angezeigt.');
      }
    } catch (_e) {
      toast.error('Token konnte nicht geladen werden');
    } finally {
      setTokenCopyLoading(false);
    }
  }, [getDevAdyenBypassToken]);

  const handleExclusiveToggle = useCallback(
    async (name, value, nextChecked) => {
      const previousPrefs = localPrefs;
      const updatedPrefs = { ...localPrefs, [name]: nextChecked ? value : 'off' };
      setLocalPrefs(updatedPrefs);
      try {
        await persistPreferences(updatedPrefs);
      } catch (_e) {
        setLocalPrefs(previousPrefs);
      }
    },
    [localPrefs, persistPreferences]
  );

  const handleModeToggle = useCallback(
    async (name, checked, onValue, offValue) => {
      const previousPrefs = localPrefs;
      const updatedPrefs = { ...localPrefs, [name]: checked ? onValue : offValue };
      setLocalPrefs(updatedPrefs);
      try {
        await persistPreferences(updatedPrefs);
      } catch (_e) {
        setLocalPrefs(previousPrefs);
      }
    },
    [localPrefs, persistPreferences]
  );

  // Toggling a sidebar counter on/off. `nextActive` true means the counter stays
  // visible (key removed from disabledCounters); false hides it (key added).
  const handleCounterToggle = useCallback(
    async (countKey, nextActive) => {
      const previousPrefs = localPrefs;
      const current = Array.isArray(localPrefs.disabledCounters) ? localPrefs.disabledCounters : [];
      const nextDisabled = nextActive
        ? current.filter(key => key !== countKey)
        : current.includes(countKey)
          ? current
          : [...current, countKey];
      const updatedPrefs = { ...localPrefs, disabledCounters: nextDisabled };
      setLocalPrefs(updatedPrefs);
      try {
        await persistPreferences(updatedPrefs);
      } catch (_e) {
        setLocalPrefs(previousPrefs);
      }
    },
    [localPrefs, persistPreferences]
  );

  const handleOpenResetDialog = useCallback(() => setOpenResetDialog(true), []);
  const handleCloseResetDialog = useCallback(() => setOpenResetDialog(false), []);
  const handleConfirmReset = useCallback(() => {
    try {
      authCtx?.resetDevice?.();
      toast.success('Gerät wurde zurückgesetzt');
    } catch (_e) {
      toast.error('Zurücksetzen fehlgeschlagen');
    } finally {
      setOpenResetDialog(false);
    }
  }, [authCtx]);

  const handleConfirmGrantSuperAdminRights = useCallback(async () => {
    try {
      setSuperAdminGrantLoading(true);
      await grantSuperAdminAllRights();
      toast.success(
        'Der superAdmin-Rolle wurden alle Rechte zugewiesen. Änderungen werden beim nächsten Anmelden wirksam.'
      );
      setSuperAdminGrantDialogOpen(false);
    } catch (_e) {
      toast.error('Fehler beim Zuweisen der Rechte an die superAdmin-Rolle');
    } finally {
      setSuperAdminGrantLoading(false);
    }
  }, [grantSuperAdminAllRights]);

  const handleConfirmGrantRoleRights = useCallback(async () => {
    if (!pendingGrantRole) return;
    try {
      setRoleGrantLoading(true);
      await grantRoleAllRights(pendingGrantRole);
      toast.success(
        `Der Rolle „${pendingGrantRole}“ wurden alle Rechte zugewiesen. Änderungen werden beim nächsten Anmelden wirksam.`
      );
      setPendingGrantRole(null);
    } catch (_e) {
      toast.error(`Fehler beim Zuweisen der Rechte an die Rolle „${pendingGrantRole}“`);
    } finally {
      setRoleGrantLoading(false);
    }
  }, [pendingGrantRole, grantRoleAllRights]);

  const handleWebauthnSelfRegister = useCallback(async () => {
    const credentialName = newCredentialName.trim();
    if (!credentialName) {
      toast.error('Bitte einen Namen für den Key eingeben');
      return;
    }
    try {
      setRegisteringCredential(true);
      const startResponse = await webauthnRegisterStartAuth();
      const registration = await startRegistration({ optionsJSON: startResponse.options });
      await webauthnRegisterFinishAuth({
        challenge: startResponse.challenge,
        credential: registration,
        credentialName,
      });
      setNewCredentialName('');
      await reloadWebauthnData();
      toast.success('Passkey erfolgreich registriert');
    } catch (error) {
      if (error?.code === 'WEBAUTHN_SECURITY_KEY_REQUIRED') {
        toast.error('Für diese Pflichtrolle ist ein externer Security Key (z. B. YubiKey) erforderlich');
      } else {
        toast.error('Fehler bei der Passkey-Registrierung');
      }
      throw error;
    } finally {
      setRegisteringCredential(false);
    }
  }, [newCredentialName, reloadWebauthnData, webauthnRegisterFinishAuth, webauthnRegisterStartAuth]);

  const formatDateTime = useCallback(value => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('de-DE');
  }, []);

  const handleDeleteCredential = useCallback(
    async credentialId => {
      if (!credentialId) return;
      const isMandatoryLastCredential =
        webauthnStatus?.mandatory === true && (webauthnCredentials?.length || 0) <= 1;
      if (isMandatoryLastCredential) {
        toast.error('Bei Pflichtrolle kann der letzte Passkey nicht gelöscht werden');
        return;
      }
      try {
        setDeletingCredentialId(credentialId);
        await deleteWebauthnCredential(credentialId);
        await reloadWebauthnData();
        toast.success('Passkey erfolgreich gelöscht');
      } catch (error) {
        toast.error('Fehler beim Löschen des Passkeys');
        throw error;
      } finally {
        setDeletingCredentialId('');
      }
    },
    [deleteWebauthnCredential, reloadWebauthnData, webauthnCredentials?.length, webauthnStatus?.mandatory]
  );

  const loadLaserZoneEntertainments = useCallback(async () => {
    if (slotRecalcEntertainmentsLoaded || slotRecalcEntertainmentsLoading) return;
    try {
      setSlotRecalcEntertainmentsLoading(true);
      const allEntertainments = await getEntertainments(true);
      const laserZones = (Array.isArray(allEntertainments) ? allEntertainments : [])
        .filter(e => e.entertainmentName === 'LaserZone');
      setSlotRecalcEntertainments(laserZones);
      setSlotRecalcEntertainmentsLoaded(true);
      if (laserZones.length === 1) {
        setSlotRecalcSelectedId(laserZones[0]._id);
      }
    } catch (_e) {
      toast.error('Fehler beim Laden der Entertainments');
    } finally {
      setSlotRecalcEntertainmentsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotRecalcEntertainmentsLoaded, slotRecalcEntertainmentsLoading]);

  const handleOpenSlotRecalcDialog = useCallback(async () => {
    setSlotRecalcDialogOpen(true);
    if (!slotRecalcEntertainmentsLoaded) {
      await loadLaserZoneEntertainments();
    }
  }, [slotRecalcEntertainmentsLoaded, loadLaserZoneEntertainments]);

  const handleCloseSlotRecalcDialog = useCallback(() => {
    setSlotRecalcDialogOpen(false);
  }, []);

  const handleConfirmSlotRecalc = useCallback(async () => {
    let entertainmentId;

    if (hasSlotRecalcFull) {
      entertainmentId = slotRecalcSelectedId;
    } else if (hasSlotRecalcOwn) {
      const ownLz = slotRecalcEntertainments[0];
      entertainmentId = ownLz?._id;
      if (!entertainmentId && slotRecalcEntertainments.length === 0) {
        await loadLaserZoneEntertainments();
        return;
      }
    }

    if (!entertainmentId) {
      toast.error('Bitte ein Entertainment auswählen');
      return;
    }

    const today = new Date();
    const day = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    try {
      setSlotRecalcLoading(true);
      await sendRequest({
        url: `/api/v1/booking/slots/recalculate/by/entertainment/${entertainmentId}/day/${day}`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
      });
      const entName = slotRecalcEntertainments.find(e => e._id === entertainmentId)?.longName || entertainmentId;
      toast.success(`Slot-Neuberechnung für ${entName} am ${day} gestartet`);
      setSlotRecalcDialogOpen(false);
    } catch (_e) {
      toast.error('Fehler beim Starten der Slot-Neuberechnung');
    } finally {
      setSlotRecalcLoading(false);
    }
  }, [hasSlotRecalcFull, hasSlotRecalcOwn, slotRecalcSelectedId, slotRecalcEntertainments, loadLaserZoneEntertainments, sendRequest, authCtx]);

  const loadMyDeviceRequests = useCallback(async () => {
    if (!canRequestDeviceType) return;
    try {
      const results = await getMyDeviceTypeRequests();
      setMyDeviceRequests(Array.isArray(results) ? results : []);
    } catch (_e) {
      // toast handled in hook
    }
  }, [canRequestDeviceType, getMyDeviceTypeRequests]);

  const loadMyDevices = useCallback(async () => {
    if (!canRequestDeviceType) return;
    try {
      const results = await getMyDevices();
      setMyDevices(Array.isArray(results) ? results : []);
    } catch (_e) {
      // toast handled in hook
    }
  }, [canRequestDeviceType, getMyDevices]);

  useEffect(() => {
    if (authCtx?.isLoggedIn && canRequestDeviceType) {
      loadMyDeviceRequests();
      loadMyDevices();
    }
  }, [authCtx?.isLoggedIn, canRequestDeviceType, loadMyDeviceRequests, loadMyDevices]);

  const handleRevokeMyDevice = useCallback(
    async deviceId => {
      try {
        await revokeMyDevice(deviceId);
        toast.success('Gerät wurde entfernt');
        await loadMyDevices();
      } catch (_e) {
        // toast handled in hook
      }
    },
    [revokeMyDevice, loadMyDevices]
  );

  const [checkingActivation, setCheckingActivation] = useState(false);
  const hasPendingDeviceActivation =
    Boolean(authCtx?.requestDeviceToken) && !authCtx?.deviceToken;

  const handleCheckDeviceActivation = useCallback(async () => {
    setCheckingActivation(true);
    try {
      await authCtx?.checkDeviceActivation?.();
      await loadMyDeviceRequests();
      await loadMyDevices();
    } catch (_e) {
      // handled in context
    } finally {
      setCheckingActivation(false);
    }
  }, [authCtx, loadMyDeviceRequests, loadMyDevices]);

  const handleOpenDeviceRequestDialog = useCallback(() => {
    setDeviceRequestType('counter');
    setDeviceRequestLabel('');
    setDeviceRequestReason('');
    setDeviceRequestDialogOpen(true);
  }, []);

  const handleSubmitDeviceRequest = useCallback(async () => {
    setDeviceRequestSubmitting(true);
    try {
      const res = await requestDeviceType({
        requestedDeviceType: deviceRequestType,
        label: deviceRequestLabel.trim(),
        reason: deviceRequestReason.trim(),
      });
      if (res?.requestDeviceToken) {
        authCtx.setRequestDeviceToken(res.requestDeviceToken);
      }
      toast.success(
        'Gerätetyp-Anpassung angefragt. Nach Freigabe durch die IT wird der Gerätetyp beim nächsten Anmelden automatisch aktiviert.'
      );
      setDeviceRequestDialogOpen(false);
      await loadMyDeviceRequests();
    } catch (_e) {
      // toast handled in hook
    } finally {
      setDeviceRequestSubmitting(false);
    }
  }, [
    requestDeviceType,
    deviceRequestType,
    deviceRequestLabel,
    deviceRequestReason,
    authCtx,
    loadMyDeviceRequests,
  ]);

  const deviceRequestStatusLabel = status => {
    if (status === 'pending') return 'offen';
    if (status === 'approved') return 'freigegeben';
    if (status === 'rejected') return 'abgelehnt';
    return status || '-';
  };

  const deviceRequestStatusColor = status => {
    if (status === 'pending') return 'warning';
    if (status === 'approved') return 'success';
    if (status === 'rejected') return 'default';
    return 'default';
  };

  // Renders a single preference switch row (handles the various switch variants).
  const renderSwitchList = items => (
    <List disablePadding>
      {(items ?? []).map((item, idx) => (
        <Box key={item.value ? `${item.key}:${item.value}` : item.key}>
          <ListItem sx={{ px: 0, py: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 1, sm: 2 },
                width: '100%',
              }}
            >
              <Box sx={{ flexGrow: 1, pr: 2, minWidth: 0 }}>
                <Typography variant='body1'>{item.label}</Typography>
                {item.hint && (
                  <Typography variant='caption' color='text.secondary' display='block'>
                    {item.hint}
                  </Typography>
                )}
                {showCtrlShiftFHint &&
                  (item.key === 'ctrlFOverrideInDataViews' ||
                    (item.key === 'ctrlFRedirectTarget' &&
                      (item.value === 'sidebarMenu' || item.value === 'headerSearch'))) && (
                    <Typography variant='caption' color='text.secondary'>
                      Hinweis: Mit Strg+Umschalt+F kannst du die Menüleiste durchsuchen.
                    </Typography>
                  )}
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                {item.type === 'switch' && (
                  <Switch
                    color='success'
                    checked={localPrefs[item.key] !== false}
                    onChange={(_, checked) => handleToggle(item.key, checked)}
                  />
                )}
                {item.type === 'inverseSwitch' && (
                  <Switch
                    color='success'
                    checked={localPrefs[item.key] !== true}
                    onChange={(_, checked) => handleToggle(item.key, !checked)}
                  />
                )}
                {item.type === 'modeSwitch' && (
                  <Switch
                    color='success'
                    checked={(localPrefs[item.key] || 'token') === 'pin'}
                    onChange={(_, checked) => handleModeToggle(item.key, checked, 'pin', 'token')}
                  />
                )}
                {item.type === 'exclusiveSwitch' && (
                  <Switch
                    color='success'
                    checked={localPrefs[item.key] === item.value}
                    onChange={(_, checked) => handleExclusiveToggle(item.key, item.value, checked)}
                  />
                )}
                {item.type === 'deviceSwitch' && (
                  <Switch
                    color='success'
                    checked={suppressPizzaWarnings}
                    onChange={(_, checked) => setSuppressPizzaWarnings(checked)}
                  />
                )}
              </Box>
            </Box>
          </ListItem>
          {idx < (items?.length ?? 0) - 1 && <Divider sx={{ my: 1.5 }} />}
        </Box>
      ))}
    </List>
  );

  // Renders the sub-groups inside a category. The sub-group header is only shown
  // when a category bundles more than one group, otherwise the accordion title
  // already names it.
  const renderSubgroups = subgroups =>
    subgroups.map((group, i) => (
      <Box key={group.title} sx={{ mb: i < subgroups.length - 1 ? 3 : 0 }}>
        {subgroups.length > 1 && (
          <Typography variant='subtitle1' color='text.secondary' sx={subSectionTitleSx}>
            {group.title}
          </Typography>
        )}
        {renderSwitchList(group.items)}
        {i < subgroups.length - 1 && <Divider sx={{ my: 2 }} />}
      </Box>
    ));

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 2 }}>
      <Paper
        sx={{
          p: 2,
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Typography variant='h6'>Einstellungen</Typography>
        </Box>
        <Typography
          variant='subtitle2'
          gutterBottom
          color='text.secondary'
          sx={{ mb: { xs: 2, md: 3 } }}
        >
          {userName ? `Angemeldet als ${userName}` : ''}
        </Typography>

        {/* Darstellung */}
        <Accordion disableGutters elevation={0} sx={sectionAccordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
            <Typography sx={{ fontWeight: 600 }}>Darstellung</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControl component='fieldset' variant='standard'>
              <FormLabel component='legend' sx={{ color: 'text.secondary', fontSize: 14, mb: 1 }}>
                Farbschema
              </FormLabel>
              <RadioGroup
                row
                value={colorModePreference}
                onChange={async e => {
                  try {
                    await setColorModePreference(e.target.value);
                  } catch (_err) {
                    toast.error('Speichern der Einstellungen fehlgeschlagen');
                  }
                }}
              >
                <FormControlLabel value='light' control={<Radio color='success' />} label='Hell' />
                <FormControlLabel value='dark' control={<Radio color='success' />} label='Dunkel' />
                <FormControlLabel value='system' control={<Radio color='success' />} label='System' />
              </RadioGroup>
            </FormControl>
          </AccordionDetails>
        </Accordion>

        {/* Switch-based preference categories */}
        {settingsCategories.map(category => (
          <Accordion key={category.id} disableGutters elevation={0} sx={sectionAccordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
              <Typography sx={{ fontWeight: 600 }}>{category.title}</Typography>
            </AccordionSummary>
            <AccordionDetails>{renderSubgroups(category.subgroups)}</AccordionDetails>
          </Accordion>
        ))}

        {/* Sidebar counters — only the counters this user holds the right to hide */}
        {counterToggleItems.length > 0 ? (
          <Accordion disableGutters elevation={0} sx={sectionAccordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
              <Typography sx={{ fontWeight: 600 }}>Seitenleisten-Zähler</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                Lege fest, welche Zähler (Zahlen-Symbole) in der Seitenleiste angezeigt werden.
                Deaktivierte Zähler werden ausgeblendet.
              </Typography>
              <List disablePadding>
                {counterToggleItems.map((counter, idx) => {
                  const isActive = !(
                    Array.isArray(localPrefs.disabledCounters) &&
                    localPrefs.disabledCounters.includes(counter.key)
                  );
                  return (
                    <Box key={counter.key}>
                      <ListItem sx={{ px: 0, py: 1.5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: { xs: 1, sm: 2 },
                            width: '100%',
                          }}
                        >
                          <Box sx={{ flexGrow: 1, pr: 2, minWidth: 0 }}>
                            <Typography variant='body1'>{counter.label}</Typography>
                          </Box>
                          <Box sx={{ flexShrink: 0 }}>
                            <Switch
                              color='success'
                              checked={isActive}
                              onChange={(_, checked) => handleCounterToggle(counter.key, checked)}
                            />
                          </Box>
                        </Box>
                      </ListItem>
                      {idx < counterToggleItems.length - 1 && <Divider sx={{ my: 1.5 }} />}
                    </Box>
                  );
                })}
              </List>
            </AccordionDetails>
          </Accordion>
        ) : null}

        {/* Sicherheit */}
        <Accordion disableGutters elevation={0} sx={sectionAccordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
            <Typography sx={{ fontWeight: 600 }}>Sicherheit</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List disablePadding>
              <Box>
                <ListItem sx={{ px: 0, py: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      width: '100%',
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant='body1'>
                        {webauthnStatus?.enforceCrossPlatformAuthenticator === true
                          ? 'Security Key registrieren'
                          : 'Passkey registrieren'}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {webauthnStatus?.enforceCrossPlatformAuthenticator === true
                          ? 'Für deine Rolle ist ein externer Security Key erforderlich (z. B. YubiKey).'
                          : 'Hinterlege einen Passkey für passwortlose Anmeldung.'}
                      </Typography>
                      {webauthnStatus?.mandatory &&
                        webauthnStatus?.enrolled !== true &&
                        Number.isFinite(Number(webauthnStatus?.daysRemaining)) && (
                          <Typography variant='caption' color='warning.main'>
                            Pflichtrolle aktiv: noch {Number(webauthnStatus.daysRemaining)} Tag(e) bis
                            zur verpflichtenden Anmeldung ohne Passwort.
                          </Typography>
                        )}
                      <Box sx={{ mt: 1.5, maxWidth: 420 }}>
                        <TextField
                          label='Name des Passkeys'
                          size='small'
                          fullWidth
                          value={newCredentialName}
                          onChange={e => setNewCredentialName(e.target.value)}
                          placeholder='z. B. Security Key Büro'
                        />
                      </Box>
                    </Box>
                    <Button
                      variant='outlined'
                      onClick={handleWebauthnSelfRegister}
                      disabled={registeringCredential}
                    >
                      Registrieren
                    </Button>
                  </Box>
                </ListItem>
                <Divider />
              </Box>
            </List>
            <Box sx={{ mt: 2 }}>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                Registrierte Passkeys
              </Typography>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Transport</TableCell>
                    <TableCell>Erstellt</TableCell>
                    <TableCell>Zuletzt genutzt</TableCell>
                    <TableCell align='right'>Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {webauthnCredentials.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ color: 'text.secondary' }}>
                        Noch kein Passkey registriert.
                      </TableCell>
                    </TableRow>
                  ) : (
                    webauthnCredentials.map((entry, index) => {
                      const transports = Array.isArray(entry?.transports) ? entry.transports : [];
                      const deleteDisabled =
                        deletingCredentialId === entry?.credentialId ||
                        (webauthnStatus?.mandatory === true && webauthnCredentials.length <= 1);
                      return (
                        <TableRow key={entry?.credentialId || `credential-${index}`}>
                          <TableCell>{entry?.name || 'Ohne Name'}</TableCell>
                          <TableCell>{transports.length > 0 ? transports.join(', ') : '-'}</TableCell>
                          <TableCell>{formatDateTime(entry?.createdAt)}</TableCell>
                          <TableCell>{formatDateTime(entry?.lastUsedAt)}</TableCell>
                          <TableCell align='right'>
                            <Button
                              color='error'
                              size='small'
                              variant='text'
                              disabled={deleteDisabled}
                              onClick={() => handleDeleteCredential(entry?.credentialId)}
                            >
                              Löschen
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              {webauthnStatus?.mandatory === true && webauthnCredentials.length <= 1 && (
                <Typography variant='caption' color='warning.main'>
                  Bei Pflichtrolle muss mindestens ein registrierter Passkey bestehen bleiben.
                </Typography>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Werkzeuge & Entwicklung (only when at least one tool is available) */}
        {showToolsSection ? (
          <Accordion disableGutters elevation={0} sx={sectionAccordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
              <Typography sx={{ fontWeight: 600 }}>Werkzeuge & Entwicklung</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {showDevAdyenBypass ? (
                <Box sx={{ mb: showSlotRecalc ? 3 : 0 }}>
                  <Typography variant='subtitle1' color='text.secondary' sx={subSectionTitleSx}>
                    Dev Tools
                  </Typography>
                  <List disablePadding>
                    <Box>
                      <ListItem sx={{ px: 0, py: 1.5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            width: '100%',
                            gap: { xs: 1, sm: 2 },
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant='body1'>Adyen im Frontend bypassen</Typography>
                            <Typography variant='body2' color='text.secondary'>
                              Überspringt den Adyen-Checkout und autorisiert Zahlungen serverseitig. Nur in Dev/Staging.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                              <Button
                                size='small'
                                variant='outlined'
                                disabled={!Boolean(localPrefs?.devBypassAdyen) || tokenCopyLoading}
                                onClick={copyDevBypassToken}
                              >
                                Token kopieren
                              </Button>
                            </Box>
                          </Box>
                          <Switch
                            checked={Boolean(localPrefs?.devBypassAdyen)}
                            onChange={e => handleToggle('devBypassAdyen', e.target.checked)}
                          />
                        </Box>
                      </ListItem>
                    </Box>
                  </List>
                </Box>
              ) : null}

              {showSlotRecalc ? (
                <Box>
                  {showDevAdyenBypass && <Divider sx={{ my: 2 }} />}
                  <Typography variant='subtitle1' color='text.secondary' sx={subSectionTitleSx}>
                    LaserTag Slots
                  </Typography>
                  <List disablePadding>
                    <Box>
                      <ListItem sx={{ px: 0, py: 1.5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            width: '100%',
                            gap: { xs: 1, sm: 2 },
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant='body1'>Slot-Zuordnung neu berechnen</Typography>
                            <Typography variant='body2' color='text.secondary'>
                              Startet die Neuberechnung der Slot-Verteilung (playerGroupID) für den heutigen Tag.
                            </Typography>
                          </Box>
                          <Button variant='outlined' onClick={handleOpenSlotRecalcDialog}>
                            Neuberechnung starten
                          </Button>
                        </Box>
                      </ListItem>
                    </Box>
                  </List>
                </Box>
              ) : null}
            </AccordionDetails>
          </Accordion>
        ) : null}

        {/* SuperAdmin (only visible to users holding the superAdmin role) */}
        {isSuperAdmin ? (
          <Accordion disableGutters elevation={0} sx={sectionAccordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
              <Typography sx={{ fontWeight: 600 }}>SuperAdmin</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List disablePadding>
                <Box>
                  <ListItem sx={{ px: 0, py: 1.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        width: '100%',
                        gap: { xs: 1, sm: 2 },
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant='body1'>Alle Rechte zuweisen</Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Weist der Rolle „superAdmin“ alle verfügbaren Oberrechte als
                          Standardrecht auf allen Gerätetypen (Firma, Kasse, privat) zu.
                          Untergeordnete Rechte sind dadurch automatisch eingeschlossen.
                        </Typography>
                      </Box>
                      <Button
                        variant='outlined'
                        color='warning'
                        onClick={() => setSuperAdminGrantDialogOpen(true)}
                        disabled={superAdminGrantLoading}
                      >
                        Alle Rechte zuweisen
                      </Button>
                    </Box>
                  </ListItem>
                </Box>
                {ADDITIONAL_GRANTABLE_ROLES.map(role => (
                  <Box key={role.name}>
                    <ListItem sx={{ px: 0, py: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          width: '100%',
                          gap: { xs: 1, sm: 2 },
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant='body1'>
                            Alle Rechte zuweisen ({role.label})
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            Weist der Rolle „{role.label}“ alle verfügbaren Oberrechte als
                            Standardrecht auf allen Gerätetypen (Firma, Kasse, privat) zu.
                            Untergeordnete Rechte sind dadurch automatisch eingeschlossen.
                          </Typography>
                        </Box>
                        <Button
                          variant='outlined'
                          color='warning'
                          onClick={() => setPendingGrantRole(role.name)}
                          disabled={roleGrantLoading}
                        >
                          Alle Rechte zuweisen
                        </Button>
                      </Box>
                    </ListItem>
                  </Box>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ) : null}

        {/* Geräteverwaltung */}
        {canRequestDeviceType ? (
          <Accordion disableGutters elevation={0} sx={sectionAccordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
              <Typography sx={{ fontWeight: 600 }}>Geräteverwaltung</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List disablePadding>
                <Box>
                  <ListItem sx={{ px: 0, py: 1.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        width: '100%',
                        gap: { xs: 1, sm: 2 },
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant='body1'>Gerätetypanpassung anfordern</Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Beantrage, dieses Gerät als Kassensystem, Firmengerät oder privates Gerät
                          einzustufen. Die IT prüft die Anfrage. Nach Freigabe wird der Gerätetyp beim
                          nächsten Anmelden automatisch für dieses Gerät aktiviert.
                        </Typography>
                      </Box>
                      <Button variant='outlined' onClick={handleOpenDeviceRequestDialog}>
                        Anpassung anfordern
                      </Button>
                    </Box>
                  </ListItem>
                </Box>
              </List>

              {hasPendingDeviceActivation && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant='body2'>
                      Für dieses Gerät liegt eine offene Anfrage vor. Sobald die IT sie freigegeben
                      hat, kannst du die Aktivierung hier prüfen (sonst geschieht das automatisch
                      bei der nächsten Anmeldung).
                    </Typography>
                  </Box>
                  <Button
                    variant='contained'
                    onClick={handleCheckDeviceActivation}
                    disabled={checkingActivation}
                  >
                    {checkingActivation ? <CircularProgress size={20} /> : 'Freigabe prüfen'}
                  </Button>
                </Box>
              )}

              {myDeviceRequests.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant='subtitle1' color='text.secondary' sx={subSectionTitleSx}>
                    Meine Anfragen
                  </Typography>
                  {(() => {
                    const requestHint = r =>
                      r.status === 'rejected' && r.rejectionReason
                        ? r.rejectionReason
                        : r.status === 'approved'
                          ? 'Beim nächsten Anmelden aktiv'
                          : r.reason || '—';
                    return isNarrowDeviceTables ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {myDeviceRequests.map(r => (
                          <Paper key={r._id} variant='outlined' sx={{ p: 1.5 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <Chip
                                size='small'
                                label={deviceRequestStatusLabel(r.status)}
                                color={deviceRequestStatusColor(r.status)}
                              />
                              <Typography variant='body2' color='text.secondary'>
                                {deviceTypeLabels[r.requestedDeviceType] || r.requestedDeviceType}
                              </Typography>
                            </Box>
                            <Typography variant='body2'>
                              Gerät: {r.deviceInfo?.label || '–'}
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                              {requestHint(r)}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <Table size='small'>
                        <TableHead>
                          <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Gewünschter Typ</TableCell>
                            <TableCell>Gerät</TableCell>
                            <TableCell>Hinweis</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {myDeviceRequests.map(r => (
                            <TableRow key={r._id}>
                              <TableCell>
                                <Chip
                                  size='small'
                                  label={deviceRequestStatusLabel(r.status)}
                                  color={deviceRequestStatusColor(r.status)}
                                />
                              </TableCell>
                              <TableCell>
                                {deviceTypeLabels[r.requestedDeviceType] || r.requestedDeviceType}
                              </TableCell>
                              <TableCell>{r.deviceInfo?.label || '–'}</TableCell>
                              <TableCell sx={{ color: 'text.secondary' }}>
                                {requestHint(r)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    );
                  })()}
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography variant='subtitle1' color='text.secondary' sx={subSectionTitleSx}>
                  Meine registrierten Geräte
                </Typography>
                {myDevices.length === 0 ? (
                  <Typography variant='body2' color='text.secondary'>
                    Für dich sind aktuell keine Geräte registriert.
                  </Typography>
                ) : isNarrowDeviceTables ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {myDevices.map(d => {
                      const isCurrent =
                        d.deviceInfo?.installId && d.deviceInfo.installId === currentInstallId;
                      return (
                        <Paper key={d._id} variant='outlined' sx={{ p: 1.5 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography variant='body2' sx={{ fontWeight: 600 }}>
                              {deviceTypeLabels[d.deviceType] || d.deviceType}
                            </Typography>
                            {isCurrent && <Chip size='small' color='info' label='Dieses Gerät' />}
                          </Box>
                          <Typography variant='body2'>Gerät: {d.label || '–'}</Typography>
                          <Typography variant='caption' color='text.secondary' display='block'>
                            Registriert: {formatDateTime(d.registeredAt)}
                          </Typography>
                          <Typography variant='caption' color='text.secondary' display='block'>
                            Zuletzt genutzt: {formatDateTime(d.lastUsedAt)}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                            <Button
                              color='error'
                              size='small'
                              variant='text'
                              onClick={() => handleRevokeMyDevice(d._id)}
                            >
                              Entfernen
                            </Button>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                ) : (
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Typ</TableCell>
                        <TableCell>Gerät</TableCell>
                        <TableCell>Registriert</TableCell>
                        <TableCell>Zuletzt genutzt</TableCell>
                        <TableCell align='right'>Aktion</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {myDevices.map(d => {
                        const isCurrent =
                          d.deviceInfo?.installId &&
                          d.deviceInfo.installId === currentInstallId;
                        return (
                          <TableRow key={d._id}>
                            <TableCell>
                              {deviceTypeLabels[d.deviceType] || d.deviceType}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{d.label || '–'}</span>
                                {isCurrent && (
                                  <Chip size='small' color='info' label='Dieses Gerät' />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{formatDateTime(d.registeredAt)}</TableCell>
                            <TableCell>{formatDateTime(d.lastUsedAt)}</TableCell>
                            <TableCell align='right'>
                              <Button
                                color='error'
                                size='small'
                                variant='text'
                                onClick={() => handleRevokeMyDevice(d._id)}
                              >
                                Entfernen
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ) : null}

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button color='error' variant='outlined' onClick={handleOpenResetDialog}>
            Gerät zurücksetzen
          </Button>
        </Box>
      </Paper>

      <Dialog open={openResetDialog} onClose={handleCloseResetDialog}>
        <DialogTitle>Gerät zurücksetzen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Dadurch werden alle lokalen Anmeldedaten (Tokens, zwischengespeicherte Anfragen)
            gelöscht und Sie werden abgemeldet. Fortfahren?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog}>Abbrechen</Button>
          <Button color='error' variant='contained' onClick={handleConfirmReset}>
            Zurücksetzen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deviceRequestDialogOpen}
        onClose={() => setDeviceRequestDialogOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Gerätetypanpassung anfordern</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Wähle, als welcher Gerätetyp dieses Gerät eingestuft werden soll. Die IT prüft die
            Anfrage zusammen mit der Gerätesignatur und gibt sie frei oder lehnt sie ab.
          </DialogContentText>
          <FormControl fullWidth size='small' sx={{ mb: 2 }}>
            <InputLabel>Gerätetyp</InputLabel>
            <Select
              value={deviceRequestType}
              label='Gerätetyp'
              onChange={e => setDeviceRequestType(e.target.value)}
            >
              <MenuItem value='counter'>{deviceTypeLabels.counter}</MenuItem>
              <MenuItem value='company'>{deviceTypeLabels.company}</MenuItem>
              <MenuItem value='private'>{deviceTypeLabels.private}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label='Gerätebezeichnung (optional)'
            placeholder='z.B. Kassen-PC Empfang'
            value={deviceRequestLabel}
            onChange={e => setDeviceRequestLabel(e.target.value)}
            fullWidth
            size='small'
            sx={{ mb: 2 }}
          />
          <TextField
            label='Begründung (optional)'
            placeholder='z.B. Dieses Gerät wird dauerhaft an der Kasse genutzt'
            value={deviceRequestReason}
            onChange={e => setDeviceRequestReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeviceRequestDialogOpen(false)}
            disabled={deviceRequestSubmitting}
          >
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={handleSubmitDeviceRequest}
            disabled={deviceRequestSubmitting}
          >
            {deviceRequestSubmitting ? <CircularProgress size={20} /> : 'Anfordern'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={superAdminGrantDialogOpen}
        onClose={() => {
          if (!superAdminGrantLoading) setSuperAdminGrantDialogOpen(false);
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Alle Rechte der superAdmin-Rolle zuweisen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Der Rolle „superAdmin“ werden alle verfügbaren Oberrechte als Standardrecht auf
            allen Gerätetypen (Firma, Kasse, privat) zugewiesen. Bestehende erweiterte Rechte
            bleiben erhalten. Änderungen werden beim nächsten Anmelden wirksam. Fortfahren?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSuperAdminGrantDialogOpen(false)}
            disabled={superAdminGrantLoading}
          >
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='warning'
            onClick={handleConfirmGrantSuperAdminRights}
            disabled={superAdminGrantLoading}
          >
            {superAdminGrantLoading ? <CircularProgress size={20} /> : 'Alle Rechte zuweisen'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingGrantRole)}
        onClose={() => {
          if (!roleGrantLoading) setPendingGrantRole(null);
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Alle Rechte der Rolle „{pendingGrantRole}“ zuweisen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Der Rolle „{pendingGrantRole}“ werden alle verfügbaren Oberrechte als Standardrecht
            auf allen Gerätetypen (Firma, Kasse, privat) zugewiesen. Bestehende erweiterte Rechte
            bleiben erhalten. Änderungen werden beim nächsten Anmelden wirksam. Fortfahren?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingGrantRole(null)} disabled={roleGrantLoading}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='warning'
            onClick={handleConfirmGrantRoleRights}
            disabled={roleGrantLoading}
          >
            {roleGrantLoading ? <CircularProgress size={20} /> : 'Alle Rechte zuweisen'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tokenDialogOpen} onClose={() => setTokenDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Adyen-BYPASS Token</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Kopiere den Token und speichere ihn im Booking-Frontend unter localStorage-Key{' '}
            <Box component='span' sx={{ fontWeight: 600 }}>
              fzDevAdyenBypassToken
            </Box>
            .
          </DialogContentText>
          <Box
            component='pre'
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              overflowX: 'auto',
              fontSize: 12,
              userSelect: 'text',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {tokenDialogValue}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTokenDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={slotRecalcDialogOpen} onClose={handleCloseSlotRecalcDialog} maxWidth='sm' fullWidth>
        <DialogTitle>Slot-Zuordnung neu berechnen</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Die Slot-Verteilung (playerGroupID) wird für den heutigen Tag neu berechnet.
            Bestehende Zuordnungen werden überschrieben.
          </DialogContentText>
          {hasSlotRecalcFull ? (
            slotRecalcEntertainmentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <FormControl fullWidth size='small'>
                <InputLabel>Entertainment</InputLabel>
                <Select
                  value={slotRecalcSelectedId}
                  label='Entertainment'
                  onChange={e => setSlotRecalcSelectedId(e.target.value)}
                >
                  {slotRecalcEntertainments.map(ent => (
                    <MenuItem key={ent._id} value={ent._id}>{ent.longName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )
          ) : (
            <Typography variant='body2'>
              {slotRecalcEntertainments.length === 1
                ? `Entertainment: ${slotRecalcEntertainments[0].longName}`
                : slotRecalcEntertainmentsLoading
                  ? 'Lade Entertainment...'
                  : 'Kein LaserZone Entertainment zugeordnet.'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSlotRecalcDialog} disabled={slotRecalcLoading}>Abbrechen</Button>
          <Button
            variant='contained'
            onClick={handleConfirmSlotRecalc}
            disabled={
              slotRecalcLoading ||
              slotRecalcEntertainmentsLoading ||
              (hasSlotRecalcFull && !slotRecalcSelectedId) ||
              (!hasSlotRecalcFull && slotRecalcEntertainments.length === 0)
            }
          >
            {slotRecalcLoading ? <CircularProgress size={20} /> : 'Neuberechnung starten'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
